import { Check, CircleAlert, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

function getStatusInfo(procedure, children) {
    const match = children.find(
        (child) => child.type === 'procedure_report' && child.procedureTemplateId === procedure.uniq_id,
    );

    if (!match) {
        return { status: 'todo', label: i18n.t('folders:aFaire'), matchChild: null, completedBy: null, completedAt: null };
    }

    if (match.status === 'completed') {
        return {
            status: 'completed',
            label: i18n.t('folders:finalise'),
            matchChild: match,
            completedBy: match.realizedBy || match.createdByName,
            completedAt: match.createdAt,
        };
    }

    return {
        status: 'draft',
        label: i18n.t('folders:brouillon'),
        matchChild: match,
        completedBy: match.realizedBy || match.createdByName,
        completedAt: match.updatedAt || match.createdAt,
    };
}

export function getCompletedCount(mandatoryProcedures, children) {
    return mandatoryProcedures.filter((p) => getStatusInfo(p, children).status === 'completed').length;
}

export default function ListMandatoryProcedures({ mandatoryProcedures = [], children = [], onNavigate, onCreate }) {
    // Abonne le composant aux changements de langue : les libellés viennent de
    // getStatusInfo(), qui traduit via i18n.t() au moment du rendu.
    useTranslation();
    const handleClick = (procedure, info) => {
        if (info.matchChild && onNavigate) {
            onNavigate(info.matchChild);
        } else if (!info.matchChild && onCreate) {
            onCreate(procedure.uniq_id);
        }
    };

    return (
        <div className="overflow-y-auto flex-1 p-4 space-y-2">
            {mandatoryProcedures.map((procedure, index) => {
                const info = getStatusInfo(procedure, children);

                return (
                    <div
                        key={procedure.uniq_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleClick(procedure, info)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleClick(procedure, info);
                            }
                        }}
                        className={cn(
                            'flex items-center gap-3 rounded-xl px-3.5 py-3.5 border border-border transition-all cursor-pointer',
                            'hover:border-success hover:shadow-sm bg-card',
                        )}
                    >
                        {/* Status indicator */}
                        <div className="flex items-center justify-center w-7 h-7 shrink-0">
                            {info.status === 'completed' ? (
                                <div className="w-7 h-7 bg-success rounded-full flex items-center justify-center">
                                    <Check className="h-4 w-4 text-white" strokeWidth={2.5} />
                                </div>
                            ) : info.status === 'draft' ? (
                                <div className="w-7 h-7 bg-warning/20 rounded-full flex items-center justify-center">
                                    <CircleAlert className="h-4 w-4 text-warning" />
                                </div>
                            ) : (
                                <div className="w-7 h-7 border-2 border-muted-foreground/30 rounded-full flex items-center justify-center">
                                    <span className="text-[11px] text-muted-foreground/50">{index + 1}</span>
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                            <TooltipProvider delayDuration={300}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <p
                                            className={cn(
                                                'text-sm truncate',
                                                info.status === 'todo' ? 'text-muted-foreground' : 'text-foreground',
                                            )}
                                        >
                                            {procedure.name}
                                        </p>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" align="start">
                                        <p>{procedure.name}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            {procedure.type === 'admin' && procedure.creator?.company && (
                                <span className="inline-block mt-1 text-[10px] font-medium text-badge-invitation bg-badge-invitation/10 px-1.5 py-0.5 rounded-full leading-none whitespace-nowrap">
                                    {procedure.creator.company}
                                </span>
                            )}
                            {info.completedBy && (
                                <p className="text-[11px] text-muted-foreground mt-0.5">
                                    {info.completedBy}
                                    {info.completedAt ? ` · ${info.completedAt}` : ''}
                                </p>
                            )}
                        </div>

                        {/* Badge + chevron */}
                        <div className="flex items-center gap-1.5 shrink-0">
                            <span
                                className={cn(
                                    'px-2 py-0.5 rounded-md text-[11px] font-medium',
                                    info.status === 'completed' && 'bg-success/10 text-success',
                                    info.status === 'draft' && 'bg-warning/10 text-warning',
                                    info.status === 'todo' && 'bg-muted text-muted-foreground',
                                )}
                            >
                                {info.label}
                            </span>
                            <ChevronRight className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
